// src/components/Gantt/GanttRow.jsx
// Одна строка задачи: подпись слева + полоса справа.
// Геометрия (left/width/fill-width) - через CSS-переменные на ref.
// Цвета полосы - через классы gbar--prio-* / gbar--closed / gbar--cancelled,
// определены в bundle.css.
import { memo, useCallback, useLayoutEffect, useRef } from "react";
import EmployeeCardLink from "../EmployeeCardLink";
import FloatingMenu from "../FloatingMenu";
import { buildTaskMenu } from "../menus";
import { TASK_STATUSES, PRIORITIES } from "../../utils/constants";
import { fmtD, fmtDMY } from "../../utils/date";
import { computeBarGeometry, isOverdue } from "./geometry";

const GanttBar = memo(function GanttBar({
  anchorProps,
  task,
  geo,
  fillWidth,
  overdue,
  canDrag,
  onOpen,
  onKey,
  onDrag,
  ariaLabel,
  tooltip,
  showLabel,
  labelText,
  vac,
}) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--bar-left", `${geo.left}px`);
    el.style.setProperty("--bar-width", `${geo.width}px`);
    el.style.setProperty("--fill-width", `${fillWidth}%`);
  }, [geo.left, geo.width, fillWidth]);

  const colorClass =
    task.status === "closed"
      ? "gbar--closed"
      : task.status === "cancelled"
        ? "gbar--cancelled"
        : `gbar--prio-${task.priority || "mid"}`;

  const className = [
    "gbar",
    colorClass,
    overdue ? "overdue" : "",
    canDrag ? "gbar-draggable" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      {...anchorProps}
      ref={ref}
      className={className}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={onOpen}
      onKeyDown={onKey}
      onMouseDown={canDrag ? onDrag : undefined}
      title={tooltip}
    >
      <div className="gbar-fill" />
      {showLabel && labelText && <span className="gbar-pct">{labelText}</span>}
      {vac && (
        <span className="gbar-vac" aria-hidden="true">
          🏖
        </span>
      )}
    </div>
  );
});

function GanttRowImpl({
  task,
  level,
  hasChildren,
  expanded,
  onToggle,
  indices,
  DW,
  employee,
  project,
  openTask,
  getTaskSpent,
  vacOverlap,
  onDragStart,
  detectDragMode,
  db,
  user,
  canEdit,
  onMove,
  onDelete,
  onCopy,
  onMakeTemplate,
  registerRowEl,
}) {
  const geo = computeBarGeometry(task, indices, DW);
  const overdue = isOverdue(task);
  const sp = getTaskSpent(task);
  const planned = task.plannedHours || 0;
  const pct = planned > 0 ? Math.min(100, (sp / planned) * 100) : 0;
  const fillWidth = pct > 0 ? Math.max(pct, 2) : 0;

  const isClosed = task.status === "closed";
  const isCancelled = task.status === "cancelled";

  const showLabel =
    isClosed ||
    isCancelled ||
    geo.width > 80 ||
    (planned > 0 && geo.width > 35);

  const labelText = isClosed
    ? "Закрыта"
    : isCancelled
      ? "Отмена"
      : planned > 0
        ? `${Math.round(pct)}%`
        : "";

  const vac = employee
    ? vacOverlap(employee.id, task.start, task.deadline)
    : null;

  const tooltip = [
    task.title,
    `Проект: ${project?.code || "-"}`,
    `Статус: ${TASK_STATUSES[task.status]?.label || task.status}`,
    `Приоритет: ${PRIORITIES[task.priority]?.label || task.priority}`,
    `План: ${task.plannedHours ?? "-"} ч, Факт: ${sp} ч`,
    `Срок: ${fmtD(task.start)} - ${fmtD(task.deadline)}`,
    ...(employee ? [`Исполнитель: ${employee.last} ${employee.first}`] : []),
    ...(vac ? [`🏖 В отпуске ${fmtDMY(vac.start)}–${fmtDMY(vac.end)}`] : []),
    ...(overdue ? ["🔴 Просрочено"] : []),
  ].join("\n");

  const ariaLabel = `Задача: ${task.title}. Статус: ${TASK_STATUSES[task.status]?.label || task.status}`;

  const setRowRef = useCallback(
    (el) => {
      registerRowEl(task.id, el);
    },
    [registerRowEl, task.id],
  );

  // Обработка клика мышью.
  // Игнорируем синтетические клики от клавиатуры (у них e.detail === 0),
  // чтобы модалка не открывалась дважды.
  const handleClick = useCallback(
    (e) => {
      if (e.detail === 0) return;
      openTask(task.id);
    },
    [openTask, task.id],
  );

  // Обработка клавиатуры.
  const handleKey = useCallback(
    (e) => {
      // Игнорируем, если событие всплыло от кнопки "Развернуть/Свернуть"
      if (e.target !== e.currentTarget) return;
      // Игнорируем автоповтор при зажатом Enter
      if (e.repeat) return;

      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        e.stopPropagation(); // Не даём событию всплывать
        openTask(task.id);
      }
    },
    [openTask, task.id],
  );

  const handleToggle = useCallback(
    (e) => {
      e.stopPropagation();
      onToggle(task.id);
    },
    [onToggle, task.id],
  );

  const handleDrag = useCallback(
    (e) => {
      onDragStart(e, task, geo, detectDragMode(e));
    },
    [onDragStart, detectDragMode, task, geo],
  );

  const canDrag = canEdit(task) && !task.dependencyId;
  const menuItems = buildTaskMenu({
    task,
    user,
    db,
    openTask,
    onMove,
    onDelete,
    onCopy,
    onMakeTemplate,
  });

  const rowClass = `gantt-row${level > 0 ? " gantt-row-child" : ""}`;

  if (!Number.isFinite(geo.left) || !Number.isFinite(geo.width)) {
    console.warn("bad geo", { task: task.id, indices, DW, geo });
  }

  return (
    <div ref={setRowRef} className={rowClass}>
      <div
        className="gantt-label"
        data-level={level}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onClick={handleClick}
        onKeyDown={handleKey}
        title={tooltip}
      >
        <div className="flex items-center gap-1">
          {hasChildren && (
            <button
              type="button"
              className={`gantt-expand-btn${expanded ? " expanded" : ""}`}
              onClick={handleToggle}
              aria-label={expanded ? "Свернуть" : "Развернуть"}
              title={expanded ? "Свернуть" : "Развернуть"}
            >
              ▶
            </button>
          )}
          <span
            className={`gtitle${task.status === "cancelled" ? " dim" : ""}`}
          >
            {task.title}
          </span>
          {overdue && (
            <span
              className="gantt-overdue-dot"
              title="Просрочено"
              aria-label="Просрочено"
            />
          )}
        </div>
        <span className="gsub">
          {employee && <EmployeeCardLink employee={employee} size="xs" />}
          {" · "}
          {`${task.plannedHours ?? "-"} ч · ${TASK_STATUSES[task.status]?.label || task.status}`}
        </span>
      </div>

      <div className="gantt-track">
        <FloatingMenu items={menuItems}>
          {({ anchorProps }) => (
            <GanttBar
              anchorProps={anchorProps}
              task={task}
              geo={geo}
              fillWidth={fillWidth}
              overdue={overdue}
              canDrag={canDrag}
              onOpen={handleClick}
              onKey={handleKey}
              onDrag={handleDrag}
              ariaLabel={ariaLabel}
              tooltip={tooltip}
              showLabel={showLabel}
              labelText={labelText}
              vac={vac}
            />
          )}
        </FloatingMenu>
      </div>
    </div>
  );
}

export const GanttRow = memo(GanttRowImpl);
